import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet';
import { FileFormat, PayoutReportItem } from '../../gql-types.generated';
import { PayoutReportDisplayName } from '../../util/PayoutReportDisplayName';
import { selectTenantAccount } from '../app/AppSlice';
import { createPayoutReport } from '../home/HomeActions';
import {
  capturePayoutReport,
  capturePayoutReportCancelledInFlight,
  capturePayoutReportError,
  capturePayoutReportInFlight,
  selectPayoutReport,
  selectPayoutReportError,
  selectPayoutReportInFlight,
} from '../home/HomeSlice';
import LoadingMask from '../../components/LoadingMask';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexFlow: 'column nowrap',
      flex: 1,
      height: '100%',
      minHeight: 350,
    },
    header: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'start',
      padding: theme.spacing(2.5),
    },
    grid: {
      padding: theme.spacing(3),
    },
    yearFilters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    yearChipSelected: {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.uxBlue.activated,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: theme.palette.uxBlue.activated,
      },
    },
    reportList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    monthAccordion: {
      border: `1px solid ${theme.palette.uxGrey.border}`,
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      // Suppress MUI's default divider pseudo-element so the bordered cards do
      // not gain a doubled line where they meet.
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    monthSummary: {
      '& .MuiAccordionSummary-content': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
      },
    },
    monthCount: {
      color: theme.palette.text.secondary,
    },
    monthDetails: {
      padding: theme.spacing(0, 1, 1),
    },
    downloadCell: {
      width: 160,
    },
    downloadLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    downloadLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      cursor: 'pointer',
      fontWeight: 500,
    },
    downloadIcon: {
      fontSize: '1.1rem',
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: 'center',
      color: theme.palette.text.secondary,
    },
    errorBanner: {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 3),
    },
    errorIcon: {
      marginRight: 4,
    },
  }),
);

const Reports: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const tenantAccount = useSelector(selectTenantAccount);
  const payoutReport = useSelector(selectPayoutReport);
  const payoutReportError = useSelector(selectPayoutReportError);
  const isFetchingPayoutUrl = useSelector(selectPayoutReportInFlight);

  const payoutReports = tenantAccount?.payoutReports;
  const downloadRef = useRef<HTMLAnchorElement>(null);
  // The payout report id currently being downloaded, so we know which resolved URI to act on.
  const [activeReportId, setActiveReportId] = useState<string | null>(null);

  // Reset the shared payout report state. createPayoutReport leaves the in-flight
  // flag set on success, so it must be cleared here once the download is handled.
  const clearPayoutReportState = () => {
    dispatch(capturePayoutReport(undefined));
    dispatch(capturePayoutReportError(undefined));
    dispatch(capturePayoutReportInFlight(false));
  };

  // Distinct years present in the payout reports, most recent first.
  const years = useMemo(() => {
    const yearSet = new Set<number>();
    (payoutReports || []).forEach(report => {
      yearSet.add(DateTime.fromISO(report.endTime).toUTC().year);
    });
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [payoutReports]);

  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  // Default the selected year to the most recent year with reports.
  useEffect(() => {
    if (years.length > 0 && (selectedYear === undefined || !years.includes(selectedYear))) {
      setSelectedYear(years[0]);
    }
  }, [years]);

  // Reports for the selected year, grouped by month, most recent month first and
  // most recent report first within each month.
  //
  // Grouping is on endTime, matching how the year chips are derived, so a report
  // never lands under a month its own label does not mention. Reports are not all
  // daily - the feed mixes daily, weekly and monthly intervals - so a month can
  // hold a mix. PayoutReportDisplayName already prefixes W/E/M/E to disambiguate.
  const monthGroups = useMemo(() => {
    if (selectedYear === undefined) return [];
    const byMonth = new Map<number, PayoutReportItem[]>();
    (payoutReports || []).forEach(report => {
      const end = DateTime.fromISO(report.endTime).toUTC();
      if (end.year !== selectedYear) return;
      const bucket = byMonth.get(end.month);
      if (bucket) bucket.push(report);
      else byMonth.set(end.month, [report]);
    });
    return Array.from(byMonth.entries())
      .sort(([a], [b]) => b - a)
      .map(([month, reports]) => ({
        month,
        // Luxon months are 1-based, so this needs no offset.
        label: DateTime.utc(selectedYear, month, 1).toFormat('MMMM yyyy'),
        reports: reports.slice().sort((a, b) => (DateTime.fromISO(a.endTime) > DateTime.fromISO(b.endTime) ? -1 : 1)),
      }));
  }, [payoutReports, selectedYear]);

  // Which month accordions are open. The most recent month is expanded by default
  // so the common case - checking the latest payout - costs no clicks.
  const [expandedMonths, setExpandedMonths] = useState<number[]>([]);

  // Extracted rather than inlined into the dependency array below: a complex
  // expression there trips react-hooks/exhaustive-deps, and CRA's build promotes
  // that warning to an error under CI=true.
  const monthKeys = monthGroups.map(group => group.month).join(',');

  useEffect(() => {
    // Keyed on the year and the month set so switching years re-opens the newest
    // month rather than leaving every accordion collapsed.
    setExpandedMonths(monthGroups.length > 0 ? [monthGroups[0].month] : []);
  }, [selectedYear, monthKeys]);

  const handleMonthToggle = (month: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedMonths(current => (isExpanded ? [...current, month] : current.filter(open => open !== month)));
  };

  const handleDownload = (payoutReportId?: string | null, format?: FileFormat) => {
    if (payoutReportId && format) {
      setActiveReportId(payoutReportId);
      dispatch(createPayoutReport(payoutReportId, format));
    }
  };

  // Once the access URI for the requested report resolves, trigger the browser download.
  useEffect(() => {
    if (payoutReport?.uri && activeReportId && downloadRef.current) {
      downloadRef.current.href = payoutReport.uri;
      downloadRef.current.click();
      setActiveReportId(null);
      clearPayoutReportState();
    }
  }, [payoutReport, activeReportId, dispatch]);

  // On unmount, cancel any in-flight generation and reset the shared state.
  useEffect(
    () => () => {
      dispatch(capturePayoutReportCancelledInFlight(true));
      clearPayoutReportState();
    },
    [dispatch],
  );

  return (
    <Paper className={classes.root} role="region" aria-label="Reports">
      <Helmet>
        <meta name="ai:viewId" content="reports"></meta>
        <meta name="ai:viewDescription" content="Aptean Pay Merchant Portal - Reports"></meta>
        <title>Aptean Pay Merchant Portal - Reports</title>
      </Helmet>
      <LoadingMask loading={isFetchingPayoutUrl} />
      <a ref={downloadRef} style={{ display: 'none' }} />
      <Box className={classes.header}>
        <Typography variant="title">Reports</Typography>
      </Box>
      <Divider />
      {payoutReportError && (
        <Box className={classes.errorBanner}>
          <ErrorIcon className={classes.errorIcon} />
          {`${payoutReportError?.message || 'Unable to generate payout report'}. Please try again later!`}
        </Box>
      )}
      <Grid className={classes.grid} container direction="column">
        {years.length > 0 && (
          <Box className={classes.yearFilters} data-cy="report-year-filters">
            {years.map(year => (
              <Chip
                key={year}
                label={year}
                variant="outlined"
                clickable
                onClick={() => setSelectedYear(year)}
                className={selectedYear === year ? classes.yearChipSelected : undefined}
                data-cy={`report-year-${year}`}
              />
            ))}
          </Box>
        )}
        {monthGroups.length > 0 ? (
          <Box className={classes.reportList} data-cy="report-list">
            {monthGroups.map(group => (
              <Accordion
                key={group.month}
                className={classes.monthAccordion}
                expanded={expandedMonths.includes(group.month)}
                onChange={handleMonthToggle(group.month)}
                disableGutters
                elevation={0}
                data-cy={`report-month-${selectedYear}-${String(group.month).padStart(2, '0')}`}
              >
                <AccordionSummary
                  className={classes.monthSummary}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`report-month-panel-${group.month}`}
                  id={`report-month-header-${group.month}`}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {group.label}
                  </Typography>
                  <Typography variant="body2" className={classes.monthCount} data-cy="report-month-count">
                    {group.reports.length === 1 ? '1 report' : `${group.reports.length} reports`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.monthDetails} id={`report-month-panel-${group.month}`}>
                  {/* Two columns today. Kept as a table rather than a list because the
                      payout amount is the likely next column, and the schema does not
                      expose it yet (it exists on the payout report document but not on
                      PayoutReportItem). A "Period" column was removed deliberately:
                      startTime/endTime, payoutCreateTime and payoutCompleteTime all
                      collapse to the same calendar date for ~98% of production reports,
                      so any of them merely repeats the Report column. */}
                  <Table size="small" aria-label={`Payout reports for ${group.label}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Report</TableCell>
                        <TableCell className={classes.downloadCell}>Download</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.reports.map(report => (
                        <TableRow key={report.payoutReportId ?? report.endTime} data-cy="report-item" hover>
                          <TableCell>{PayoutReportDisplayName(report.startTime, report.endTime)}</TableCell>
                          <TableCell className={classes.downloadCell}>
                            {report.payoutReportId ? (
                              <Box className={classes.downloadLinks}>
                                {[FileFormat.Csv, FileFormat.Pdf].map(format => (
                                  <Link
                                    key={format}
                                    component="button"
                                    type="button"
                                    underline="hover"
                                    className={classes.downloadLink}
                                    disabled={isFetchingPayoutUrl}
                                    onClick={() => handleDownload(report.payoutReportId, format)}
                                    data-cy={`report-download-${format}`}
                                  >
                                    <FileDownloadOutlinedIcon className={classes.downloadIcon} />
                                    {format === FileFormat.Pdf ? 'PDF' : 'CSV'}
                                  </Link>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Unavailable
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Box className={classes.emptyState} data-cy="report-empty">
            <Typography variant="body1">No reports available.</Typography>
          </Box>
        )}
      </Grid>
    </Paper>
  );
};

export default Reports;
